import { FormEvent, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { supabase } from "./lib/supabase";
import "./stand-notification-center.css";

type Notification = {
  id: number;
  kind: string;
  title: string;
  body: string;
  action_kind: "task" | "conversation" | "support_ticket" | null;
  action_entity_id: string | null;
  read_at: string | null;
  created_at: string;
};
type Ticket = {
  id: string;
  public_reference: string;
  subject: string;
  category: string;
  priority: string;
  status: string;
  created_by: string;
  updated_at: string;
};
type SupportMessage = {
  id: number;
  ticket_id: string;
  author_side: "stand" | "support";
  body: string;
  created_at: string;
};
type Delivery = {
  message_id: number;
  ticket_id: string;
  status: string;
};

const statusLabels: Record<string, string> = {
  open: "Aberto",
  waiting_stand: "Aguardar resposta do stand",
  waiting_support: "Aguardar suporte",
  resolved: "Resolvido",
  closed: "Encerrado",
};

export function StandNotificationCenter({
  organizationId,
  enabled,
  onOpenTask,
}: {
  organizationId: string;
  enabled: boolean;
  onOpenTask: (taskId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"notifications" | "support">("notifications");
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const load = async () => {
    const [notificationResult, ticketResult, messageResult, deliveryResult] =
      await Promise.all([
        supabase
          .from("stand_notifications")
          .select(
            "id,kind,title,body,action_kind,action_entity_id,read_at,created_at",
          )
          .eq("organization_id", organizationId)
          .is("archived_at", null)
          .order("created_at", { ascending: false })
          .limit(50),
        supabase
          .from("stand_support_tickets")
          .select(
            "id,public_reference,subject,category,priority,status,created_by,updated_at",
          )
          .eq("organization_id", organizationId)
          .order("updated_at", { ascending: false }),
        supabase
          .from("stand_support_messages")
          .select("id,ticket_id,author_side,body,created_at")
          .eq("organization_id", organizationId)
          .order("created_at"),
        supabase
          .from("stand_support_email_deliveries")
          .select("message_id,ticket_id,status")
          .eq("organization_id", organizationId),
      ]);
    const error =
      notificationResult.error ??
      ticketResult.error ??
      messageResult.error ??
      deliveryResult.error;
    if (error) return setNotice(error.message);
    setNotifications((notificationResult.data ?? []) as Notification[]);
    setTickets((ticketResult.data ?? []) as Ticket[]);
    setMessages((messageResult.data ?? []) as SupportMessage[]);
    setDeliveries((deliveryResult.data ?? []) as Delivery[]);
  };
  useEffect(() => {
    if (!enabled) return;
    void load();
    const timer = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(timer);
  }, [organizationId, enabled]);

  const unread = notifications.filter((item) => !item.read_at).length;
  const activeTicket =
    tickets.find((item) => item.id === selectedTicket) ?? null;
  const activeMessages = useMemo(
    () => messages.filter((item) => item.ticket_id === selectedTicket),
    [messages, selectedTicket],
  );
  const act = async (notification: Notification) => {
    await supabase.rpc("mark_stand_notification", {
      p_notification_id: notification.id,
      p_action: "read",
    });
    if (
      notification.action_kind === "conversation" &&
      notification.action_entity_id
    ) {
      window.dispatchEvent(
        new CustomEvent("vorno:open-conversation", {
          detail: { conversationId: notification.action_entity_id },
        }),
      );
      setOpen(false);
    } else if (
      notification.action_kind === "task" &&
      notification.action_entity_id
    ) {
      onOpenTask(notification.action_entity_id);
      setOpen(false);
    } else if (
      notification.action_kind === "support_ticket" &&
      notification.action_entity_id
    ) {
      setTab("support");
      setSelectedTicket(notification.action_entity_id);
    }
    await load();
  };
  const createTicket = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    setNotice("A criar o pedido de suporte…");
    const { data: ticketId, error } = await supabase.rpc(
      "create_stand_support_ticket",
      {
        p_organization_id: organizationId,
        p_subject: String(data.get("subject") || ""),
        p_category: String(data.get("category") || "other"),
        p_priority: String(data.get("priority") || "normal"),
        p_message: String(data.get("message") || ""),
      },
    );
    setBusy(false);
    if (error) return setNotice(error.message);
    form.reset();
    setCreating(false);
    setSelectedTicket(String(ticketId));
    setNotice(
      "Pedido criado. A conversa também ficou registada na caixa de email de Testes.",
    );
    await load();
  };
  const reply = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!activeTicket) return;
    const form = event.currentTarget;
    const message = String(new FormData(form).get("message") || "");
    setBusy(true);
    setNotice("A enviar a mensagem…");
    const { error } = await supabase.rpc("reply_stand_support_ticket", {
      p_ticket_id: activeTicket.id,
      p_message: message,
      p_status: "waiting_support",
    });
    setBusy(false);
    if (error) return setNotice(error.message);
    form.reset();
    setNotice("Mensagem guardada e refletida na caixa de email de Testes.");
    await load();
  };
  if (!enabled) return null;
  return (
    <>
      <button
        type="button"
        className={`stand-notification-launcher ${unread ? "has-unread" : ""}`}
        onClick={() => setOpen((value) => !value)}
        aria-label="Notificações e suporte"
      >
        <span className="stand-notification-launcher-label">
          Notificações e suporte
        </span>
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M18 9a6 6 0 0 0-12 0c0 7-3 7-3 8h18c0-1-3-1-3-8M10 21h4" />
        </svg>
        {unread > 0 && <b>{unread}</b>}
      </button>
      {open &&
        createPortal(
          <div
            className="stand-notification-overlay"
            onMouseDown={() => setOpen(false)}
          >
            <aside
              className="stand-notification-center"
              role="dialog"
              aria-modal="true"
              aria-label="Notificações e suporte"
              onMouseDown={(event) => event.stopPropagation()}
            >
          <header>
            <div>
              <p>Comunicação</p>
              <h2>Notificações e suporte</h2>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Fechar">
              ×
            </button>
          </header>
          <nav>
            <button
              className={tab === "notifications" ? "active" : ""}
              onClick={() => setTab("notifications")}
            >
              Notificações {unread > 0 && <b>{unread}</b>}
            </button>
            <button
              className={tab === "support" ? "active" : ""}
              onClick={() => setTab("support")}
            >
              Suporte
            </button>
          </nav>
          {notice && (
            <button
              className="stand-notification-notice"
              onClick={() => setNotice(null)}
            >
              {notice} ×
            </button>
          )}
          {tab === "notifications" ? (
            <div className="stand-notification-list">
              {notifications.length === 0 ? (
                <p>Não existem notificações.</p>
              ) : (
                notifications.map((notification) => (
                  <article
                    className={notification.read_at ? "" : "unread"}
                    key={notification.id}
                  >
                    <button onClick={() => void act(notification)}>
                      <span>
                        <strong>{notification.title}</strong>
                        <small>{notification.body}</small>
                        <time>
                          {new Date(notification.created_at).toLocaleString(
                            "pt-PT",
                          )}
                        </time>
                      </span>
                      {notification.action_kind && <em>Abrir</em>}
                    </button>
                    <button
                      className="archive"
                      aria-label="Arquivar notificação"
                      onClick={async () => {
                        await supabase.rpc("mark_stand_notification", {
                          p_notification_id: notification.id,
                          p_action: "archive",
                        });
                        await load();
                      }}
                    >
                      ×
                    </button>
                  </article>
                ))
              )}
            </div>
          ) : activeTicket ? (
            <div className="stand-support-conversation">
              <button
                className="stand-support-back"
                onClick={() => setSelectedTicket(null)}
              >
                ← Pedidos
              </button>
              <div className="stand-support-title">
                <strong>{activeTicket.subject}</strong>
                <small>
                  {activeTicket.public_reference} ·{" "}
                  {statusLabels[activeTicket.status]}
                </small>
              </div>
              <div className="stand-support-messages">
                {activeMessages.map((message) => (
                  <article className={message.author_side} key={message.id}>
                    <p>{message.body}</p>
                    <time>
                      {new Date(message.created_at).toLocaleString("pt-PT")}
                    </time>
                    {deliveries.some(
                      (delivery) =>
                        delivery.message_id === message.id &&
                        delivery.status === "test_delivered",
                    ) && <small>Email de Testes registado</small>}
                  </article>
                ))}
              </div>
              {!["resolved", "closed"].includes(activeTicket.status) && (
                <form onSubmit={reply}>
                  <textarea
                    name="message"
                    required
                    rows={3}
                    placeholder="Escreva uma mensagem…"
                  />
                  <button disabled={busy}>Enviar</button>
                </form>
              )}
            </div>
          ) : creating ? (
            <form className="stand-support-new" onSubmit={createTicket}>
              <label>
                <span>Assunto</span>
                <input name="subject" required minLength={3} />
              </label>
              <div>
                <label>
                  <span>Categoria</span>
                  <select name="category">
                    <option value="technical">Problema técnico</option>
                    <option value="billing">Faturação</option>
                    <option value="account">Conta</option>
                    <option value="suggestion">Sugestão</option>
                    <option value="other">Outro</option>
                  </select>
                </label>
                <label>
                  <span>Prioridade</span>
                  <select name="priority">
                    <option value="normal">Normal</option>
                    <option value="high">Alta</option>
                    <option value="urgent">Urgente</option>
                  </select>
                </label>
              </div>
              <label>
                <span>Mensagem</span>
                <textarea name="message" required rows={6} />
              </label>
              <footer>
                <button type="button" onClick={() => setCreating(false)}>
                  Cancelar
                </button>
                <button className="primary" disabled={busy}>
                  Guardar
                </button>
              </footer>
            </form>
          ) : (
            <div className="stand-support-list">
              <button
                className="stand-support-new-button"
                onClick={() => setCreating(true)}
              >
                + Novo pedido
              </button>
              {tickets.length === 0 ? (
                <p>Não existem pedidos de suporte.</p>
              ) : (
                tickets.map((ticket) => (
                  <button
                    key={ticket.id}
                    onClick={() => setSelectedTicket(ticket.id)}
                  >
                    <span>
                      <strong>{ticket.subject}</strong>
                      <small>
                        {ticket.public_reference} ·{" "}
                        {statusLabels[ticket.status]}
                      </small>
                      <time>
                        {new Date(ticket.updated_at).toLocaleString("pt-PT")}
                      </time>
                    </span>
                    <b>›</b>
                  </button>
                ))
              )}
            </div>
          )}
            </aside>
          </div>,
          document.body,
        )}
    </>
  );
}
